import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, GripVerticalIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FormField } from './FormField';
export interface CaptureField {
  id: string;
  type: 'preset' | 'custom';
  preset?: 'name' | 'email' | 'phone' | 'address' | 'company';
  label?: string;
  description?: string;
  required: boolean;
}
interface CaptureFieldsEditorProps {
  fields: CaptureField[];
  onChange: (fields: CaptureField[]) => void;
}
export function CaptureFieldsEditor({
  fields,
  onChange
}: CaptureFieldsEditorProps) {
  const {
    t
  } = useTranslation('common');
  const [newFieldType, setNewFieldType] = useState<'preset' | 'custom'>('preset');
  const [newFieldPreset, setNewFieldPreset] = useState<'name' | 'email' | 'phone' | 'address' | 'company'>('name');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const handleAddField = () => {
    if (newFieldType === 'preset') {
      // Check if this preset already exists
      const presetExists = fields.some(field => field.type === 'preset' && field.preset === newFieldPreset);
      if (presetExists) {
        alert(`A field for "${getPresetLabel(newFieldPreset)}" already exists.`);
        return;
      }
      const newField: CaptureField = {
        id: `preset-${newFieldPreset}-${Date.now()}`,
        type: 'preset',
        preset: newFieldPreset,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
    } else if (newFieldType === 'custom') {
      if (!newFieldLabel.trim()) {
        alert('Please enter a label for the custom field.');
        return;
      }
      const newField: CaptureField = {
        id: `custom-${Date.now()}`,
        type: 'custom',
        label: newFieldLabel,
        description: newFieldDescription,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
      setNewFieldLabel('');
      setNewFieldDescription('');
    }
    setShowAddForm(false);
  };
  const handleRemoveField = (fieldId: string) => {
    onChange(fields.filter(field => field.id !== fieldId));
  };
  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === fields.length - 1) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex];
    newFields[targetIndex] = temp;
    onChange(newFields);
  };
  const handleToggleRequired = (fieldId: string) => {
    onChange(fields.map(field => field.id === fieldId ? {
      ...field,
      required: !field.required
    } : field));
  };
  const getPresetLabel = (preset: 'name' | 'email' | 'phone' | 'address' | 'company'): string => {
    const labels = {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      address: 'Billing Address',
      company: 'Company Name'
    };
    return labels[preset];
  };
  return <div className="space-y-4" data-id="element-14215">
      <div className="flex justify-between items-center mb-2" data-id="element-14216">
        <h3 className="text-sm font-medium text-gray-700" data-id="element-14217">
          {t('ui.CaptureFieldsEditor.customer_information_fields')}
        </h3>
        <button type="button" onClick={() => setShowAddForm(!showAddForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-14218">
          {showAddForm ? 'Cancel' : <>
              <PlusIcon size={14} className="mr-1" data-id="element-14219" />
              {t('ui.CaptureFieldsEditor.add_field')}
            </>}
        </button>
      </div>
      {/* Add new field form */}
      {showAddForm && <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4" data-id="element-14220">
          <div className="mb-3" data-id="element-14221">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14222">
              {t('ui.CaptureFieldsEditor.field_type')}
            </label>
            <div className="flex space-x-3" data-id="element-14223">
              <label className="flex items-center" data-id="element-14224">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'preset'} onChange={() => setNewFieldType('preset')} data-id="element-14225" />

                <span className="ml-2 text-sm text-gray-700" data-id="element-14226">
                  {t('ui.CaptureFieldsEditor.preset_field')}
                </span>
              </label>
              <label className="flex items-center" data-id="element-14227">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'custom'} onChange={() => setNewFieldType('custom')} data-id="element-14228" />

                <span className="ml-2 text-sm text-gray-700" data-id="element-14229">
                  {t('ui.CaptureFieldsEditor.custom_field')}
                </span>
              </label>
            </div>
          </div>
          {newFieldType === 'preset' ? <div className="mb-3" data-id="element-14230">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14231">
                {t('ui.CaptureFieldsEditor.preset_field')}
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={newFieldPreset} onChange={e => setNewFieldPreset(e.target.value as any)} data-id="element-14232">
                <option value="name" data-id="element-14233">
                  {t('ui.CaptureFieldsEditor.full_name')}
                </option>
                <option value="email" data-id="element-14234">
                  {t('ui.CaptureFieldsEditor.email_address')}
                </option>
                <option value="phone" data-id="element-14235">
                  {t('ui.CaptureFieldsEditor.phone_number')}
                </option>
                <option value="address" data-id="element-14236">
                  {t('ui.CaptureFieldsEditor.billing_address')}
                </option>
                <option value="company" data-id="element-14237">
                  {t('ui.CaptureFieldsEditor.company_name')}
                </option>
              </select>
            </div> : <>
              <div className="mb-3" data-id="element-14238">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14239">
                  {t('ui.CaptureFieldsEditor.field_label')}
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('ui.CaptureFieldsEditor.attrs.eg_project_name')} value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} data-id="element-14240" />
              </div>
              <div className="mb-3" data-id="element-14241">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14242">
                  {t('ui.CaptureFieldsEditor.field_description_optional')}
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('ui.CaptureFieldsEditor.attrs.eg_please_provide_the_project_name')} value={newFieldDescription} onChange={e => setNewFieldDescription(e.target.value)} data-id="element-14243" />
              </div>
            </>}
          <div className="mb-3" data-id="element-14244">
            <label className="flex items-center" data-id="element-14245">
              <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={newFieldRequired} onChange={e => setNewFieldRequired(e.target.checked)} data-id="element-14246" />

              <span className="ml-2 text-sm text-gray-700" data-id="element-14247">
                {t('ui.CaptureFieldsEditor.required_field')}
              </span>
            </label>
          </div>
          <div className="flex justify-end" data-id="element-14248">
            <button type="button" onClick={handleAddField} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" data-id="element-14249">
              {t('ui.CaptureFieldsEditor.add_field')}
            </button>
          </div>
        </div>}
      {/* List of fields */}
      {fields.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-14250">
          {fields.map((field, index) => <div key={field.id} className="p-3 flex items-center" data-id="element-14251">
              <div className="flex-shrink-0 mr-2 text-gray-400" data-id="element-14252">
                <GripVerticalIcon size={16} data-id="element-14253" />
              </div>
              <div className="flex-1" data-id="element-14254">
                <div className="flex justify-between items-center" data-id="element-14255">
                  <div data-id="element-14256">
                    <span className="font-medium text-gray-800" data-id="element-14257">
                      {field.type === 'preset' ? getPresetLabel(field.preset!) : field.label}
                    </span>
                    {field.required && <span className="ml-1 text-red-500 text-xs" data-id="element-14258">*</span>}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-14259">
                    <button type="button" onClick={() => handleToggleRequired(field.id)} className={`text-xs px-2 py-1 rounded ${field.required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-14260">
                      {field.required ? 'Required' : 'Optional'}
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'up')} disabled={index === 0} className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-14261">
                      <ChevronUpIcon size={16} data-id="element-14262" />
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'down')} disabled={index === fields.length - 1} className={`text-gray-400 hover:text-gray-600 ${index === fields.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-14263">
                      <ChevronDownIcon size={16} data-id="element-14264" />
                    </button>
                    <button type="button" onClick={() => handleRemoveField(field.id)} aria-label="Remove field" className="text-gray-400 hover:text-red-500" data-id="element-14265">
                      <XIcon size={16} aria-hidden="true" data-id="element-14266" />
                    </button>
                  </div>
                </div>
                {field.type === 'custom' && field.description && <p className="text-xs text-gray-500 mt-1" data-id="element-14267">
                    {field.description}
                  </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-14268">
                  {field.type === 'preset' ? 'Standard input field' : 'Custom input field'}
                </p>
              </div>
            </div>)}
        </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-14269">
          <p className="text-gray-500" data-id="element-14270">
            {t('ui.CaptureFieldsEditor.no_capture_fields_added')}
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-14271">
            {t('ui.CaptureFieldsEditor.add_fields_to_collect_information_from')}
          </p>
        </div>}
      <div className="text-xs text-gray-500 mt-2" data-id="element-14272">
        {t('ui.CaptureFieldsEditor.these_fields_will_be_shown_on')}
      </div>
    </div>;
}